'use client'

/**
 * batch-67 middelen-inname (FO-499 §A4) - MultiPhotoUpload: meerdere
 * bewijsfoto's bij één regel (staat van een ingeleverd of uitgegeven middel).
 *
 * Bewust een EIGEN component naast `PhotoUpload`: dat is één profielfoto in
 * avatar-vorm met vervangen/verwijderen; dit is een rij miniaturen waar foto's
 * bij komen. Mobile-first: de knop opent op een telefoon direct de camera
 * (`capture="environment"`), miniaturen en knoppen zijn minimaal 44 px.
 *
 * De component bezit de UI-toestand (bezig / klaar / in de wachtrij / mislukt
 * met opnieuw proberen) en de voorbeeldweergave; de aanroeper doet de upload zelf
 * via `onUpload` en geeft de foto-id terug die het formulier meestuurt. Dat kan
 * een echte upload zijn of, zonder verbinding, een plek in de offline-wachtrij
 * (`inWachtrij`): voor het formulier maakt dat geen verschil.
 *
 * Validatie is dubbelop (hier voor snelle feedback, de API is leidend): JPEG,
 * PNG of WebP, hooguit 25 MB, hooguit `max` foto's.
 */

import * as React from 'react'
import { Camera, Loader2, RefreshCw, X } from 'lucide-react'
import { cn } from '../lib/utils'
import { controleerFotoBestand, MIDDEL_FOTO_MAX_AANTAL, MIDDEL_FOTO_MIME_TYPES } from '../lib/middel-formulier'

export type MultiPhotoUploadUitkomst = { ok: true; id: string; inWachtrij?: boolean } | { ok: false; fout: string }

export interface MultiPhotoUploadProps {
  /** Upload één foto en geef de id terug die het formulier meestuurt. */
  onUpload: (file: File) => Promise<MultiPhotoUploadUitkomst>
  /** De id's van de foto's die klaar zijn (geupload of in de wachtrij), in volgorde. */
  onChange: (ids: string[]) => void
  max?: number
  disabled?: boolean
  /** Toont de hint dat minimaal één foto nodig is. */
  verplicht?: boolean
  /** Foutmelding van het formulier (bv. "maak minimaal een foto"). */
  fout?: string | null
  label?: string
  className?: string
  /** Wordt aangeroepen terwijl er nog foto's onderweg zijn (true) en zodra dat klaar is (false). */
  onBezigChange?: (bezig: boolean) => void
}

interface Foto {
  lokaalId: string
  file: File
  previewUrl: string
  status: 'bezig' | 'klaar' | 'wachtrij' | 'fout'
  id: string | null
  fout: string | null
}

let teller = 0
function nieuwLokaalId(): string {
  teller += 1
  return `foto-${Date.now()}-${teller}`
}

export function MultiPhotoUpload({
  onUpload,
  onChange,
  max = MIDDEL_FOTO_MAX_AANTAL,
  disabled = false,
  verplicht = false,
  fout = null,
  label = 'Foto toevoegen',
  className,
  onBezigChange,
}: MultiPhotoUploadProps) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [fotos, setFotos] = React.useState<Foto[]>([])
  const [melding, setMelding] = React.useState<string | null>(null)
  // De nieuwste callbacks zonder de effecten opnieuw te laten lopen.
  const onChangeRef = React.useRef(onChange)
  const onBezigRef = React.useRef(onBezigChange)
  onChangeRef.current = onChange
  onBezigRef.current = onBezigChange

  // Geef de klaarstaande id's door zodra de lijst verandert.
  const idsSleutel = fotos
    .filter((f) => f.id && (f.status === 'klaar' || f.status === 'wachtrij'))
    .map((f) => f.id as string)
    .join(',')
  React.useEffect(() => {
    onChangeRef.current(idsSleutel ? idsSleutel.split(',') : [])
  }, [idsSleutel])

  const bezig = fotos.some((f) => f.status === 'bezig')
  React.useEffect(() => {
    onBezigRef.current?.(bezig)
  }, [bezig])

  // Voorbeeld-URL's vrijgeven bij het verlaten van het scherm.
  const fotosRef = React.useRef(fotos)
  fotosRef.current = fotos
  React.useEffect(() => {
    return () => {
      for (const f of fotosRef.current) URL.revokeObjectURL(f.previewUrl)
    }
  }, [])

  const upload = React.useCallback(
    async (foto: Foto) => {
      setFotos((lijst) => lijst.map((f) => (f.lokaalId === foto.lokaalId ? { ...f, status: 'bezig', fout: null } : f)))
      let uitkomst: MultiPhotoUploadUitkomst
      try {
        uitkomst = await onUpload(foto.file)
      } catch {
        uitkomst = { ok: false, fout: 'Uploaden mislukt. Probeer het opnieuw.' }
      }
      setFotos((lijst) =>
        lijst.map((f) => {
          if (f.lokaalId !== foto.lokaalId) return f
          return uitkomst.ok
            ? { ...f, status: uitkomst.inWachtrij ? 'wachtrij' : 'klaar', id: uitkomst.id, fout: null }
            : { ...f, status: 'fout', fout: uitkomst.fout }
        }),
      )
    },
    [onUpload],
  )

  function kies(files: FileList | null) {
    if (!files || files.length === 0) return
    setMelding(null)
    const ruimte = max - fotos.length
    const gekozen = Array.from(files)
    if (gekozen.length > ruimte) {
      setMelding(`Maximaal ${max} foto's per middel.`)
    }
    const nieuw: Foto[] = []
    for (const file of gekozen.slice(0, Math.max(0, ruimte))) {
      const probleem = controleerFotoBestand(file)
      if (probleem) {
        setMelding(probleem)
        continue
      }
      nieuw.push({
        lokaalId: nieuwLokaalId(),
        file,
        previewUrl: URL.createObjectURL(file),
        status: 'bezig',
        id: null,
        fout: null,
      })
    }
    if (nieuw.length === 0) return
    setFotos((lijst) => [...lijst, ...nieuw])
    for (const foto of nieuw) void upload(foto)
  }

  function verwijder(lokaalId: string) {
    setFotos((lijst) => {
      const weg = lijst.find((f) => f.lokaalId === lokaalId)
      if (weg) URL.revokeObjectURL(weg.previewUrl)
      return lijst.filter((f) => f.lokaalId !== lokaalId)
    })
  }

  const vol = fotos.length >= max
  const aantalKlaar = fotos.filter((f) => f.status === 'klaar' || f.status === 'wachtrij').length

  return (
    <div className={cn('space-y-2', className)}>
      <input
        ref={inputRef}
        type="file"
        accept={MIDDEL_FOTO_MIME_TYPES.join(',')}
        capture="environment"
        multiple
        className="hidden"
        disabled={disabled || vol}
        onChange={(e) => {
          kies(e.target.files)
          e.target.value = ''
        }}
      />
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || vol}
          className="inline-flex min-h-11 items-center gap-2 rounded-md border border-[#CED0C8] bg-white px-4 text-sm font-medium text-[#1A1A18] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Camera className="h-4 w-4" aria-hidden="true" />
          {label}
        </button>
        <span className="text-xs text-[#5C5C58]">
          {aantalKlaar === 0
            ? verplicht
              ? 'Minimaal een foto nodig'
              : 'Nog geen foto'
            : `${aantalKlaar} foto${aantalKlaar === 1 ? '' : "'s"}`}
        </span>
      </div>

      {fotos.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {fotos.map((f, i) => (
            <li key={f.lokaalId} className="relative">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={f.previewUrl}
                alt={`Foto ${i + 1}`}
                className={cn(
                  'h-20 w-20 rounded-md border object-cover',
                  f.status === 'fout' ? 'border-red-500 opacity-60' : 'border-[#CED0C8]',
                )}
              />
              {f.status === 'bezig' ? (
                <span className="absolute inset-0 flex items-center justify-center rounded-md bg-black/40">
                  <Loader2 className="h-5 w-5 animate-spin text-white" aria-label="Bezig met uploaden" />
                </span>
              ) : null}
              {f.status === 'wachtrij' ? (
                <span className="absolute inset-x-0 bottom-0 rounded-b-md bg-[#1A1A18]/80 px-1 py-0.5 text-center text-[10px] text-white">
                  In wachtrij
                </span>
              ) : null}
              {f.status === 'fout' ? (
                <button
                  type="button"
                  onClick={() => void upload(f)}
                  disabled={disabled}
                  aria-label={`Foto ${i + 1} opnieuw uploaden`}
                  className="absolute inset-0 flex items-center justify-center rounded-md bg-black/40"
                >
                  <RefreshCw className="h-5 w-5 text-white" aria-hidden="true" />
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => verwijder(f.lokaalId)}
                disabled={disabled}
                aria-label={`Foto ${i + 1} verwijderen`}
                className="absolute -right-2 -top-2 flex h-11 w-11 items-start justify-end"
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#1A1A18] text-white">
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {fotos.some((f) => f.status === 'fout') ? (
        <p className="text-sm text-red-700">
          {fotos.find((f) => f.status === 'fout')?.fout ?? 'Een foto is niet geupload.'} Tik op de foto om het opnieuw te proberen.
        </p>
      ) : null}
      {melding ? <p className="text-sm text-red-700">{melding}</p> : null}
      {fout ? <p className="text-sm text-red-700">{fout}</p> : null}
    </div>
  )
}
